import AddCircleOutlined from "@mui/icons-material/AddCircleOutlined";
import OpenInNew from "@mui/icons-material/OpenInNew";
import {
  Button,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { useFollowUpScheduleText } from "../../../follow-up/hooks/use-follow-up-schedule-text";
import { convertMomentScheduleToFollowUpSchedule } from "../../../follow-up/server/moment-to-follow-up";
import { statusToPill } from "../../../moment-card/utils";

import type { UseSupabaseLoaderState } from "../../../../server/supabase/hooks/types";
import type {
  FollowUp,
  MomentExistingFollowUps,
} from "../../../follow-up/server/follow-up-service";
import type { FollowUpMomentDetails } from "../../../follow-up/server/types";
import type { FC } from "react";

export const FollowUpMomentsTable: FC<{
  followUpDataLoader: UseSupabaseLoaderState<FollowUpMomentDetails | null>;
  onCreateFollowUp?: (followUpData: FollowUpMomentDetails) => void;
}> = ({ followUpDataLoader: { loading, data }, onCreateFollowUp }) => (
  <Stack direction="column" spacing={2}>
    {loading ? (
      <Skeleton variant="rectangular" height={100} />
    ) : (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Follow-Up name</TableCell>
            <TableCell>Follow-Up reason</TableCell>
            <TableCell>Schedule</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(data?.existing_follow_ups ?? []).map((followUp) => (
            <TableRow key={followUp.id}>
              <TableCell>
                <Stack direction="row" sx={{ alignItems: "center" }}>
                  <Button
                    data-analytics-id="moment-form-follow-up-dialog-follow-up-moment-link"
                    href={`/follow-up/${followUp.id}`}
                    target="_blank"
                    endIcon={<OpenInNew />}
                  >
                    {followUp.title}
                  </Button>
                </Stack>
              </TableCell>
              <TableCell sx={{ maxWidth: 300, wordBreak: "break-word" }}>
                <FollowUpReason config={followUp.config} surveys={data?.surveys} />
              </TableCell>
              <TableCell>
                {data && <FollowUpSchedule followUp={followUp} moment={data} />}
              </TableCell>
              <TableCell>{statusToPill(followUp.status)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )}
    {onCreateFollowUp && (
      <Button
        data-analytics-id="moment-form-follow-up-dialog-create-follow-up-button"
        variant="outlined"
        onClick={() => data && onCreateFollowUp(data)}
        disabled={loading}
        loading={loading}
        sx={{ width: "fit-content" }}
        startIcon={<AddCircleOutlined />}
      >
        Create a new Follow-Up
      </Button>
    )}
  </Stack>
);

const FollowUpReason: FC<{
  config: FollowUp["config"];
  surveys?: FollowUpMomentDetails["surveys"];
}> = ({ config, surveys }) => {
  if (!config) return null;

  switch (config.type) {
    case "no_interaction":
      return "Hasn’t seen communication";
    case "no_survey_completion": {
      const survey = surveys?.find((s) => s.id === config.survey_id);
      if (!survey) return "Hasn’t completed survey";
      return `Hasn’t completed survey: ${survey.title}`;
    }
    case "link_click":
      return config.hyperlink_url ? `Clicked link: ${config.hyperlink_url}` : "Clicked any link";
    case "no_link_click":
      return config.hyperlink_url
        ? `Hasn’t clicked link: ${config.hyperlink_url}`
        : "Hasn’t clicked any link";
  }
  return undefined;
};

const FollowUpSchedule: FC<{
  moment: FollowUpMomentDetails;
  followUp: NonNullable<MomentExistingFollowUps["follow_ups"]>[number];
}> = ({ moment, followUp }) => {
  const data = convertMomentScheduleToFollowUpSchedule(moment, {
    immediate: followUp.immediate ?? false,
    schedule: followUp.schedule,
    timezone: followUp.timezone ?? null,
    schedule_event: followUp?.event_key ? { date: followUp.event_key.date } : undefined,
  });

  return useFollowUpScheduleText(data.schedule, moment);
};
